<script>
import i18n from '@/plugins/i18n';
import Properties from './properties.vue';
import Relations from './relations.vue';

export default {
    props: {
        Items: {
            type: Object,
            required: true,
        },

        CUT_NAME_THRESHOLD: {
            type: Number,
            default: 13,
        },

        CUT_FROM: {
            type: Number,
            default: 7,
        },

    },

    components: {
        Properties,
        Relations,
    },

    data() { 
        return {
            itemForProperties: null,
        }
    },

    computed: {
        GRID_VIEW_ICONS() {
            return [
                {
                    type: 'pdf',
                    icon: 'fa-solid fa-file-pdf',
                },
                {
                    type: 'doc',
                    icon: 'fa-solid fa-file-word',
                },
                {
                    type: 'png',
                    icon: 'fa-solid fa-file-image',
                },
                {
                    type: 'xls',
                    icon: 'fa-solid fa-file-excel',
                },
                {
                    type: 'jpg',
                    icon: 'fa-solid fa-file-image',
                },
                {
                    type: 'docx',
                    icon: 'fa-solid fa-file-word',
                },
                {
                    type: 'xlsx',
                    icon: 'fa-solid fa-file-excel',
                },
                {
                    type: 'csv',
                    icon: 'fa-solid fa-file-csv',
                },
            ];
        },

        DIRECTORY_ICONS() {
            return [
                {
                    id: 1,
                    icon: 'fa-solid fa-trash',
                },
                {
                    id: 2,
                    icon: 'fa-solid fa-tree-city',
                },
                {
                    id: 3,
                    icon: 'fa-solid fa-chart-line',
                },
                {
                    id: 4,
                    icon: 'fa-solid fa-folder',
                },
            ];
        },

        CONTEXT_MENU_ITEMS() {
            return [
                {
                    key: 'view',
                    text: i18n.t('documents.actions.view')
                },
                {
                    key: 'delete',
                    text: i18n.t('documents.actions.delete')
                },
                {
                    key: 'relations',
                    text: i18n.t('documents.actions.relations')
                },
                {
                    key: 'properties',
                    text: i18n.t('documents.actions.properties')
                },
            ];
        },

        DIRECTORY_CONTEXT_MENU_ITEMS() {
            return [
                {
                    key: 'view',
                    text: i18n.t('documents.actions.open')
                },
            ];
        },

        AVAILABLE_EVENTS() {
            return [
                'upload',
                'delete',
                'erase',
                'properties',
                'view',
                'relations',
            ];
        },
    },

    methods: {
        async HandleAction(key, data) {
            if (!this.AVAILABLE_EVENTS.includes(key)) {
                console.error(key + ' is not an available action!');
                return;
            }

            if (key === 'properties') {
                await this.SetItemForProperties(data);
                this.$refs.properties_popup.openPopup();
            } else if (key === 'relations') {
                await this.SetItemForProperties(data);
                this.$refs.relations_popup.openPopup();
            } else {
                this.$emit(key, data);
                this.$refs.properties_popup.startHideProcess();
                this.$refs.relations_popup.startHideProcess();
            }
        },

        async SetItemForProperties(data) {
            this.itemForProperties = data;;
        },
    },
}
</script>

<template>
    <div class="view-grid">
        <div class="documents" v-if="Items.length > 0">
            <div class="document" v-for="item in Items" :key="item.id"
                @dblclick="HandleAction('view', item)"
                v-context-menu="{items: item.is_directory ? DIRECTORY_CONTEXT_MENU_ITEMS : CONTEXT_MENU_ITEMS, onItemClick: HandleAction, data: item}"
            >
                <i :class="item.is_directory ? DIRECTORY_ICONS.find(x => x.id === Number(item.id)).icon : GRID_VIEW_ICONS.find(x => x.type === item.type).icon"></i>
                <div>
                    <span v-if="item.name.length > CUT_NAME_THRESHOLD" v-sust-tooltip="{text: item.name, bgColor: 'var(--carbon-alternative)'}">
                        {{ item.name.substring(0, CUT_FROM) + '... .' + item.type }}
                    </span>
                    <span v-else>
                        {{ item.name }}
                    </span>
                </div>
            </div>
        </div>

        <div v-else class="no-documents-linked">
            <div>
                {{ $t('documents.no_documents_here') }}
            </div>
    
            <div>
                <button class="sust-btn sust-btn-green upload-a-document" @click="HandleAction('upload')">
                    {{ $t('documents.upload_document') }}
                </button>
                <div>
                    {{ $t('documents.or_link_existing_document') }}
                </div>
            </div>
        </div>

        <sust-popup ref="properties_popup"
            :title="$t('documents.properties.header') + ` - ${itemForProperties ? itemForProperties.name : '???'}`"
            :header="true" :footer="false" size="sm" bgColor="var(--sustainfinity-faded-blue)">
            <Properties :Item="itemForProperties" @action="HandleAction($event, itemForProperties)" />
        </sust-popup>

        <sust-popup ref="relations_popup" :title="$t('documents.relations.header') + ` - ${itemForProperties ? itemForProperties.name : '???'}`" :header="true" :footer="false" size="md"
            bgColor="var(--sustainfinity-faded-blue)"
        >
            <Relations v-if="itemForProperties" :Id="itemForProperties.id"/>
        </sust-popup>
    </div>
</template>

<style lang='scss' scoped>
.view-grid {
    padding: 1em 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    > div {
        width: 100%;
    }

    > .header {
        color: var(--carbon-alternative);
        font-size: 1.4vw;
        margin-bottom: .5em;
    }
        
    > .documents {
        flex: 1;
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: flex-start;
        align-content: flex-start;
        flex-wrap: wrap;

        color: var(--carbon);
        font-size: 1.1vw;
        padding: .5em 0;

        > .document {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;

            margin: 0 .25em .25em .25em;
            padding: .25em;
            max-width: 7.5%;
            text-align: center;
    
            color: var(--carbon);
            font-size: 1.1vw;
            padding: .5em;

            border-radius: .35em;

            cursor: pointer;
            transition: color 250ms, background-color 250ms;

            &:hover {
                color: var(--carbon);
                background-color: rgba(0, 0, 0, .1);
            }

            > i {
                font-size: 2vw;
                color: var(--sustainfinity-blue);
                height: fit-content;
                padding-bottom: .1em;
            }
            
            > div {
                font-size: .8vw;
                color: var(--carbon-alternative);
            }
        }
    }
}

.no-documents-linked {
    color: var(--carbon-alternative);
    font-size: .85vw;

    >* {
        margin-bottom: .5em;
    }

    >div {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: flex-start;

        >button {
            margin-right: .5em;
            background-color: var(--carbon-alternative);
            color: whitesmoke;

            &:hover {
                background-color: var(--sustainfinity-green);
            }
        }
    }
}

.no-border {
    border: 0 !important;
}

.icon-button {
    font-size: 1vw;
    transition: color 250ms !important;

    > .fa-square-plus {
        color: var(--sustainfinity-green) !important;
    }

    > .fa-square-minus {
        color: var(--invalid-input) !important;
    }

    &:hover {
        > .fa-square-plus {
            color: var(--sustainfinity-green--hover) !important;
        }

        > .fa-square-minus {
            color: var(--invalid-input--hover) !important;
        }
    }
}
</style>